// «Dueño, insignias y pack» de una agencia:
//   · Dueño: poner a alguien como dueño por su email, con su rol de agencia
//     (users/{uid}.agencyRoles, lo único que concede acceso) y las marcas
//     antiguas que aún pintan las apps anteriores, como hace el Admin de la
//     app; y, si se quiere, quitárselo al anterior. Le llega la bienvenida si
//     la agencia está validada (el Worker la manda una vez por dueño).
//   · Insignias y verificada: las otorga TikControl (el dueño no puede).
//   · Pack de animaciones: lo pide el dueño desde su consola; aquí se da por
//     entregado.

import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch
} from '@nextui-org/react';
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDocs,
  limit as fsLimit,
  query,
  serverTimestamp,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { AgencyShared as S } from '../../lib/agencyApp';
import { toMs } from '../../lib/agencyAdmin';
import { saveAgencyPatch, sendOwnerWelcome } from '../../lib/agencyActions';

const PACK_LABELS = { none: 'Sin pack', basic: 'Básico', standard: 'Standard', advanced: 'Avanzado' };

function fmtDate(ms) {
  return ms ? new Date(ms).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

// Un usuario por su email (tal cual y en minúsculas).
async function findUserByEmail(email) {
  const clean = String(email || '').trim();
  if (!clean) return null;
  for (const value of [...new Set([clean, clean.toLowerCase()])]) {
    const snap = await getDocs(query(collection(db, 'users'), where('email', '==', value), fsLimit(2)));
    if (!snap.empty) return { uid: snap.docs[0].id, ...snap.docs[0].data() };
  }
  return null;
}

export function ownerRolePatch(agencyId) {
  return {
    [`agencyRoles.${agencyId}`]: 'owner',
    [`OWNER_${agencyId}`]: true,
    role: `OWNER_${agencyId}`,
    roles: arrayUnion(`OWNER_${agencyId}`)
  };
}

export function dropRolePatch(agencyId, user) {
  const patch = {
    [`agencyRoles.${agencyId}`]: deleteField(),
    [`OWNER_${agencyId}`]: deleteField(),
    [`MANAGER_${agencyId}`]: deleteField(),
    roles: arrayRemove(`OWNER_${agencyId}`, `MANAGER_${agencyId}`)
  };
  if (user?.role === `OWNER_${agencyId}` || user?.role === `MANAGER_${agencyId}`) patch.role = deleteField();
  return patch;
}

function OwnerSection({ agency, owners, onDone }) {
  const [email, setEmail] = useState('');
  const [found, setFound] = useState(null);
  const [searching, setSearching] = useState(false);
  const [main, setMain] = useState(true);
  const [drop, setDrop] = useState({});
  const [welcome, setWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mainOwner = agency.ownerId || agency.ownerUid || '';

  async function search() {
    setSearching(true);
    setError('');
    setFound(null);
    try {
      const u = await findUserByEmail(email);
      if (!u) setError('No hay ninguna cuenta de TikControl con ese email.');
      else setFound(u);
    } catch (e) {
      setError(e.message || 'No se ha podido buscar.');
    } finally {
      setSearching(false);
    }
  }

  async function apply() {
    if (!found) return;
    setBusy(true);
    setError('');
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'users', found.uid), ownerRolePatch(agency.id));
      for (const o of owners) {
        if (o.uid !== found.uid && drop[o.uid]) batch.update(doc(db, 'users', o.uid), dropRolePatch(agency.id, o));
      }
      const agencyPatch = { updatedAt: serverTimestamp() };
      if (main || !mainOwner) {
        agencyPatch.ownerId = found.uid;
        agencyPatch.ownerEmail = found.email || email.trim();
        // ownerUid (docs antiguos) también da la propiedad: sigue al nuevo.
        if (agency.ownerUid) agencyPatch.ownerUid = found.uid;
      } else if (drop[mainOwner]) {
        // Se le quita el rol al dueño principal sin poner a otro: deja de serlo.
        agencyPatch.ownerId = deleteField();
        agencyPatch.ownerEmail = deleteField();
        if (agency.ownerUid) agencyPatch.ownerUid = deleteField();
      }
      batch.update(doc(db, 'agencies', agency.id), agencyPatch);
      await batch.commit();
      const welcomed = welcome ? await sendOwnerWelcome(agency.id, found.uid) : '';
      setFound(null);
      setEmail('');
      setDrop({});
      onDone(`${found.email || 'La cuenta'} ya es dueño de la agencia.${welcomed}`);
    } catch (e) {
      setError(e.message || 'No se ha guardado.');
    } finally {
      setBusy(false);
    }
  }

  async function removeOwner(o) {
    if (!confirm(`¿Quitar a ${o.email || o.uid} como dueño de ${agency.name || agency.id}? Pierde el acceso a la consola de la agencia.`)) return;
    setBusy(true);
    setError('');
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'users', o.uid), dropRolePatch(agency.id, o));
      if (o.uid === mainOwner) {
        batch.update(doc(db, 'agencies', agency.id), {
          ownerId: deleteField(),
          ownerEmail: deleteField(),
          ...(agency.ownerUid ? { ownerUid: deleteField() } : {}),
          updatedAt: serverTimestamp()
        });
      }
      await batch.commit();
      onDone(`${o.email || o.uid} ya no es dueño.`);
    } catch (e) {
      setError(e.message || 'No se ha guardado.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="border-b border-default-200 pb-1">
        <p className="text-sm font-semibold">Dueño</p>
        <p className="text-xs text-default-500">Entra en la consola de la agencia con todos los permisos.</p>
      </div>
      {owners.length ? (
        <ul className="flex flex-col gap-1.5">
          {owners.map((o) => (
            <li key={o.uid} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium">{o.email || o.uid}</span>
                {o.tiktokUsername && <span className="text-default-500"> · @{o.tiktokUsername}</span>}
                {o.uid === mainOwner && <Chip size="sm" variant="flat" color="primary" className="ml-2">Principal</Chip>}
                {!o.hasRole && <Chip size="sm" variant="flat" color="warning" className="ml-2">Sin rol: no entra</Chip>}
              </span>
              <Button size="sm" variant="light" color="danger" onPress={() => removeOwner(o)} isDisabled={busy}>Quitar</Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-warning-600">No tiene dueño.</p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <Input
          size="sm"
          type="email"
          label="Email de la cuenta del nuevo dueño"
          value={email}
          onValueChange={(v) => { setEmail(v); setFound(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
          className="max-w-sm"
        />
        <Button size="sm" variant="flat" onPress={search} isLoading={searching} isDisabled={!email.trim()}>Buscar</Button>
      </div>
      {found && (
        <div className="flex flex-col gap-2 border-l-2 border-primary pl-3">
          <p className="text-sm">
            <span className="font-medium">{found.email}</span>
            {found.tiktokUsername && <span className="text-default-500"> · @{found.tiktokUsername}</span>}
            {found.agencyRoles?.[agency.id] === 'owner' && <span className="text-success-500"> · ya es dueño</span>}
            {found.agencyRoles?.[agency.id] === 'manager' && <span className="text-warning-600"> · ahora es manager: pasa a dueño</span>}
          </p>
          {mainOwner && mainOwner !== found.uid && (
            <Switch size="sm" isSelected={main} onValueChange={setMain}>Dueño principal (sustituye al de la ficha)</Switch>
          )}
          {owners.filter((o) => o.uid !== found.uid).map((o) => (
            <Checkbox key={o.uid} size="sm" isSelected={!!drop[o.uid]} onValueChange={(v) => setDrop((d) => ({ ...d, [o.uid]: v }))}>
              Quitar a {o.email || o.uid} como dueño
            </Checkbox>
          ))}
          <Checkbox size="sm" isSelected={welcome} onValueChange={setWelcome}>
            Mandarle el correo de bienvenida {agency.validated ? '' : '(saldrá cuando se valide la agencia)'}
          </Checkbox>
          <div>
            <Button size="sm" color="primary" onPress={apply} isLoading={busy}>Poner como dueño</Button>
          </div>
        </div>
      )}
      {error && <p className="text-xs text-danger-500">{error}</p>}
    </section>
  );
}

function BadgesSection({ agency, onDone }) {
  const [badges, setBadges] = useState(() => new Set(Array.isArray(agency.badges) ? agency.badges : []));
  const [verified, setVerified] = useState(agency.verified === true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toggle = (id, on) => setBadges((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });

  async function save() {
    setBusy(true);
    setError('');
    try {
      await saveAgencyPatch(agency.id, { set: { badges: [...badges], verified } });
      onDone('Insignias guardadas.');
    } catch (e) {
      setError(e.message || 'No se ha guardado.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="border-b border-default-200 pb-1">
        <p className="text-sm font-semibold">Insignias</p>
        <p className="text-xs text-default-500">Las da TikControl: el dueño no puede ponérselas.</p>
      </div>
      <Switch size="sm" isSelected={verified} onValueChange={setVerified}>Verificada (el check junto a su nombre en el Explorador)</Switch>
      <div className="flex flex-wrap gap-2">
        {(S.BADGES || []).map((b) => {
          const on = badges.has(b.id);
          return (
            <button
              type="button"
              key={b.id}
              onClick={() => toggle(b.id, !on)}
              className={`rounded-full border px-3 py-1 text-xs ${on ? 'text-white' : 'border-default-200 text-default-500'}`}
              style={on ? { borderColor: b.color, background: `${b.color}33` } : undefined}
            >
              {on ? '✓ ' : ''}{S.t(b.label, b.id)}
            </button>
          );
        })}
      </div>
      <div><Button size="sm" variant="flat" onPress={save} isLoading={busy}>Guardar insignias</Button></div>
      {error && <p className="text-xs text-danger-500">{error}</p>}
    </section>
  );
}

function PackSection({ agency, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const eff = S.agencyPlan(agency);
  const status = agency.animPackStatus || (agency.animPackDeliveredAt ? 'delivered' : (agency.animPackRequestedAt ? 'requested' : ''));
  const requested = toMs(agency.animPackRequestedAt);
  const delivered = toMs(agency.animPackDeliveredAt);

  async function deliver() {
    setBusy(true);
    setError('');
    try {
      await saveAgencyPatch(agency.id, { set: { animPackStatus: 'delivered', animPackDeliveredAt: Date.now() } });
      onDone('Pack marcado como entregado.');
    } catch (e) {
      setError(e.message || 'No se ha guardado.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="border-b border-default-200 pb-1">
        <p className="text-sm font-semibold">Pack de animaciones</p>
        <p className="text-xs text-default-500">Incluido en su plan: {PACK_LABELS[eff.animPack] || eff.animPack}. Se cambia en «Condiciones del plan».</p>
      </div>
      <p className="text-sm">
        {status === 'requested' && <>Lo ha pedido{requested ? ` el ${fmtDate(requested)}` : ''}.</>}
        {status === 'delivered' && <span className="text-success-500">Entregado{delivered ? ` el ${fmtDate(delivered)}` : ''}.</span>}
        {!status && <span className="text-default-500">Todavía no lo ha pedido (lo pide el dueño desde su consola).</span>}
      </p>
      {status === 'requested' && (
        <div><Button size="sm" color="success" variant="flat" onPress={deliver} isLoading={busy}>Marcar como entregado</Button></div>
      )}
      {error && <p className="text-xs text-danger-500">{error}</p>}
    </section>
  );
}

export default function AgencyOwnerEditor({ isOpen, onClose, agency, owners = [], onSaved }) {
  // Cada apertura empieza de cero (lo de dentro lee la agencia al montarse).
  const [round, setRound] = useState(0);
  useEffect(() => { if (isOpen) setRound((r) => r + 1); }, [isOpen]);
  if (!agency) return null;
  const done = (msg) => onSaved?.(msg);
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-0.5">
          <span>Dueño, insignias y pack</span>
          <span className="text-xs font-normal text-default-500">{agency.name || agency.id} · {agency.id}</span>
        </ModalHeader>
        <ModalBody key={round} className="flex flex-col gap-6">
          <OwnerSection agency={agency} owners={owners} onDone={done} />
          <BadgesSection agency={agency} onDone={done} />
          <PackSection agency={agency} onDone={done} />
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
